import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Pagination } from '@ermitsrl/commons/table';
import { of } from 'rxjs';

import { Router } from '@angular/router';
import { EMPTY, Observable, ReplaySubject, throwError } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { OxypeakEnvironment } from '../models/oxypeak-environment.model';
import { Team } from '../models/team.model';
import { AuthService } from './auth.service';
import { LoggerService } from './log.service';

@Injectable({
  providedIn: 'root',
})
export class TeamService {
  private userTeam$ = new ReplaySubject<Team | null>(1);
  private resolvedTeam$ = new ReplaySubject<Team>(1);
  private resolvedTeam!: Team;

  private apiUrl = `${this.env.server.url}/team`;

  // In-memory cache for team observables
  private teamRequestCache = new Map<string, Observable<Team | null>>();

  // In-memory cache for user team observable
  private userTeamRequest$: Observable<Team | null> | null = null;

  constructor(
    @Inject('env') private env: OxypeakEnvironment,
    private authService: AuthService,
    private loggerService: LoggerService,
    private router: Router,
    private http: HttpClient
  ) {}

  getTeams(httpParams: HttpParams): Observable<Pagination<Team>> {
    return this.http.get<Pagination<Team>>(this.apiUrl, {
      params: httpParams,
    });
  }

  fetchUserTeam() {
    this.getUserTeam().subscribe();
  }

  catchError(err: any) {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 404) {
        console.error("UFFF, I've not a Team configured");
        return EMPTY;
      }
      if (err.status === 401 || err.status === 403) {
        console.error("UFFF, I'm unauthorized");
        this.authService.logout();
      }

      return throwError(err);
    }
    return EMPTY;
  }

  refreshUserTeam() {
    this.fetchUserTeam();
  }

  whenUserTeam(): Observable<Team | null> {
    return this.userTeam$.asObservable();
  }

  setUserTeam(userTeam: Team | null) {
    this.userTeam$.next(userTeam);
  }

  getTeam(id: string): Observable<Team> {
    return this.http.get<Team>(`${this.apiUrl}/${id}`);
  }

  createTeam(team: Partial<Team>): Observable<Team> {
    return this.http.post<Team>(this.apiUrl, team);
  }

  updateTeam(team: Partial<Team>): Observable<Team> {
    return this.http.put<Team>(`${this.apiUrl}/${team.id}`, team).pipe(
      tap((updatedTeam) => {
        if (updatedTeam?.id) {
          this.invalidateTeamCache(updatedTeam.id); // Invalidate cache after update
          this.setResolvedTeam(updatedTeam); // Optionally update resolved team
        }
      })
    );
  }

  deleteTeam(team: Partial<Team>) {
    return this.http.delete(`${this.apiUrl}/${team.id}`).pipe(
      tap(() => {
        if (team.id) {
          this.invalidateTeamCache(team.id); // Invalidate cache after delete
        }
      })
    );
  }

  whenResolvedTeam(): Observable<Team> {
    return this.resolvedTeam$.asObservable();
  }

  setResolvedTeam(resolvedTeam: Team) {
    this.resolvedTeam = resolvedTeam;
    this.resolvedTeam$.next(resolvedTeam);
  }

  getResolvedTeam(): Team {
    return this.resolvedTeam;
  }

  resolveTeam(teamId: string): Observable<Team | null> {
    // Check if an observable for this teamId is already cached
    const cachedRequest = this.teamRequestCache.get(teamId);
    if (cachedRequest) {
      return cachedRequest;
    }

    // Create the HTTP request observable and cache it
    const request$ = this.http.get<Team>(`${this.apiUrl}/${teamId}`).pipe(
      tap((team) => {
        if (team) {
          this.setResolvedTeam(team);
        }
      }),
      catchError((err) => {
        let errorPage = '/404';
        switch (err.status) {
          case 404:
          case 403:
            errorPage = '/404';
            break;
          default:
            break;
        }
        this.router.navigate([errorPage]);
        return of(null);
      }),
      shareReplay(1) // RxJS cache: share the latest value with all subscribers
    );

    this.teamRequestCache.set(teamId, request$);
    return request$;
  }

  // Invalidate cache for a specific team
  invalidateTeamCache(teamId: string): void {
    this.teamRequestCache.delete(teamId);
  }

  // Invalidate all team cache (if needed)
  invalidateAllTeamCache(): void {
    this.teamRequestCache.clear();
  }

  // Restituisce l'observable cached della user team
  getUserTeam(): Observable<Team | null> {
    if (this.userTeamRequest$) {
      return this.userTeamRequest$;
    }

    this.userTeamRequest$ = this.http.get<Team>(`${this.apiUrl}/mine`).pipe(
      tap((team) => {
        this.loggerService.log('USER TEAM Fetched', team);
        this.setUserTeam(team);
      }),
      catchError((err) => this.catchError(err)),
      shareReplay(1)
    );

    return this.userTeamRequest$;
  }

  // Invalida la cache della user team (da chiamare dopo logout o cambio team)
  invalidateUserTeamCache(): void {
    this.userTeamRequest$ = null;
  }
}
